import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Download, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import VideoPlayer from "@/components/VideoPlayer";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Quiz = ({ courseId, lesson, result, onDone }) => {
  const [ans, setAns] = useState({});
  const submit = async () => {
    try {
      const { data } = await api.post(`/me/courses/${courseId}/lessons/${lesson.id}/quiz`, { answers: lesson.quiz.map((_, i) => ans[i] ?? -1) });
      toast.success(`Quiz score: ${data.score}%`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-lux p-6 mt-8" data-testid="lesson-quiz">
      <p className="eyebrow">Lesson quiz {result && <span className="text-gold-light normal-case">· Last score {result.score}%</span>}</p>
      {lesson.quiz.map((q, i) => (
        <fieldset key={i} className="mb-6">
          <legend className="text-white font-medium mb-3">{i + 1}. {q.question}</legend>
          <div className="space-y-2">
            {q.options.map((o, j) => (
              <label key={j} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer text-sm transition-colors ${ans[i] === j ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 text-slate-300 hover:border-white/30"}`}>
                <input type="radio" name={`q${i}`} checked={ans[i] === j} onChange={() => setAns({ ...ans, [i]: j })} className="accent-[#D4AF37]" data-testid={`quiz-q${i}-option-${j}`} />{o}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      <button onClick={submit} className="btn-gold" data-testid="quiz-submit-btn">Submit answers</button>
    </div>
  );
};

export default function CoursePlayer() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/me/learn/${slug}`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); nav(`/courses/${slug}`); }), [slug, nav]);
  useEffect(() => { load(); }, [load]);
  useSEO({ title: d?.course?.title });
  const lessons = useMemo(() => (d ? d.course.modules.flatMap((m) => m.lessons) : []), [d]);
  if (!d) return <div className="min-h-screen bg-black" />;
  const done = new Set(d.progress.filter((p) => p.completed).map((p) => p.item_id));
  const lid = params.get("lesson") || lessons.find((l) => !done.has(l.id))?.id || lessons[0]?.id;
  const lesson = lessons.find((l) => l.id === lid);
  const idx = lessons.indexOf(lesson);
  const prog = d.progress.find((p) => p.item_id === lid);
  const save = async (body) => { await api.post("/me/progress", { item_type: "lesson", item_id: lid, course_id: d.course.id, ...body }).catch(() => {}); };
  const toggleDone = async () => { await save({ completed: !done.has(lid), position: prog?.position || 0 }); load(); };

  return (
    <div className="min-h-screen bg-black lg:flex" data-testid="course-player-page">
      <aside className="lg:w-[360px] lg:h-screen lg:sticky lg:top-0 overflow-y-auto border-r border-white/10 bg-[#05070F]">
        <div className="p-6 border-b border-white/10">
          <Link to="/dashboard" className="text-sm text-slate-400 hover:text-white flex items-center gap-2" data-testid="player-back-link"><ArrowLeft size={15} />Dashboard</Link>
          <h2 className="font-serif text-xl font-semibold text-white mt-4">{d.course.title}</h2>
          <div className="mt-4 flex items-center gap-3"><Progress value={d.summary.percent} className="h-1.5 bg-white/10 [&>div]:bg-[#D4AF37]" /><span className="text-xs text-gold-light font-accent" data-testid="course-progress-percent">{d.summary.percent}%</span></div>
        </div>
        <Accordion type="multiple" defaultValue={d.course.modules.map((m) => m.id)}>
          {d.course.modules.map((m) => (
            <AccordionItem key={m.id} value={m.id} className="border-white/10">
              <AccordionTrigger className="px-6 text-sm text-white hover:no-underline">{m.title}</AccordionTrigger>
              <AccordionContent className="pb-2">
                {m.lessons.map((l) => (
                  <button key={l.id} onClick={() => setParams({ lesson: l.id })} className={`w-full text-left px-6 py-3 flex items-center gap-3 text-sm transition-colors ${l.id === lid ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`} data-testid={`lesson-item-${l.id}`}>
                    {done.has(l.id) ? <CheckCircle2 size={16} className="text-[#D4AF37] shrink-0" /> : <Circle size={16} className="shrink-0" />}{l.title}
                  </button>
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </aside>
      <main className="flex-1 p-5 sm:p-10 max-w-5xl">
        {lesson && (
          <>
            <VideoPlayer url={lesson.video_url} title={lesson.title} startAt={prog?.position || 0} onProgress={(t, dur) => save({ position: t, duration: dur })} onEnded={async (dur) => { await save({ position: dur, duration: dur, completed: true }); load(); }} />
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h1 className="font-serif text-3xl font-semibold tracking-tight text-white" data-testid="lesson-title">{lesson.title}</h1>
              <div className="flex gap-3">
                <button onClick={toggleDone} className={done.has(lid) ? "btn-outline" : "btn-gold"} data-testid="lesson-complete-btn">{done.has(lid) ? "Mark incomplete" : "Mark complete"}</button>
                {lessons[idx + 1] && <button onClick={() => setParams({ lesson: lessons[idx + 1].id })} className="btn-outline" data-testid="next-lesson-btn">Next <ChevronRight size={16} /></button>}
              </div>
            </div>
            {lesson.content && <p className="mt-6 text-slate-300 leading-relaxed whitespace-pre-line">{lesson.content}</p>}
            {lesson.attachments?.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-3" data-testid="lesson-attachments">
                {lesson.attachments.map((a) => <a key={a.url} href={fileUrl(a.url)} target="_blank" rel="noreferrer" className="btn-outline !py-2.5"><Download size={15} />{a.title}</a>)}
              </div>
            )}
            {lesson.quiz?.length > 0 && <Quiz key={lesson.id} courseId={d.course.id} lesson={lesson} result={d.quiz_results.find((q) => q.lesson_id === lid)} onDone={load} />}
          </>
        )}
      </main>
    </div>
  );
}
